import { clearAllBrowserStorage, getToken } from "../services/storageService";
import { clearGatewayCookieSession } from "./session/gatewayCookieSession";
import { keycloak } from "./keycloak";
import { APP_LOGGED_OUT_KEY } from "./session/authSession.constants";
import { clearAuthSessionQuery } from "./session/authQueryCache";
import { buildGatewayLoginUrlWithAppContext, readThisAppLoginContext } from "./session/gatewayLoginContext";
import { resolveGatewayBaseUrl, resolveGatewayLoginApiBase } from "../runtime/gatewayEnv";
import {
  isSsoPrefetchRequest,
  notifySsoPrefetchComplete,
} from "./session/ssoPrefetch";

/** Prevent focus/visibility/StrictMode from spamming select-app + login redirects. */
let ssoLoginRedirectStarted = false;

export function gatewayLoginUrl(): string {
  const fromEnv = (process.env.REACT_APP_LOGIN_URL ?? "").trim();
  if (fromEnv) return fromEnv;
  return `${resolveGatewayBaseUrl({ allowLocalFallback: true })}/login`;
}

type LogoutApiResult = {
  logoutUrl?: string;
  loginUrl?: string;
};

function parseLogoutApiResponse(data: unknown): LogoutApiResult {
  if (!data || typeof data !== "object") return {};
  const root = data as Record<string, unknown>;
  const nested =
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : null;
  const logoutUrl = String(root.logoutUrl ?? nested?.logoutUrl ?? "").trim();
  const loginUrl = String(root.loginUrl ?? nested?.loginUrl ?? "").trim();
  return {
    logoutUrl: logoutUrl || undefined,
    loginUrl: loginUrl || undefined,
  };
}

/** POST gateway logout and return Keycloak logout URL (required for second login). */
async function postGatewayLogout(): Promise<LogoutApiResult> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const returnUrl =
    typeof window !== "undefined" ? `${window.location.origin}/` : "/";

  try {
    const res = await fetch(
      `${resolveGatewayBaseUrl({ allowLocalFallback: true })}/client/auth/logout`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ url: returnUrl }),
        credentials: "include",
      },
    );
    if (!res.ok) return {};
    return parseLogoutApiResponse(await res.json());
  } catch {
    return {};
  }
}

export function setAppLoggedOutFlag(): void {
  try {
    localStorage.setItem(APP_LOGGED_OUT_KEY, "true");
  } catch {
    // ignore
  }
}

export function hasAppLoggedOutFlag(): boolean {
  try {
    return localStorage.getItem(APP_LOGGED_OUT_KEY) === "true";
  } catch {
    return false;
  }
}

export function clearAppLoggedOutFlag(): void {
  try {
    localStorage.removeItem(APP_LOGGED_OUT_KEY);
  } catch {
    // ignore
  }
}

export function clearAppSessionStorage(): void {
  const loggedOut = hasAppLoggedOutFlag();
  clearGatewayCookieSession();
  clearAllBrowserStorage();
  if (loggedOut) {
    setAppLoggedOutFlag();
  }
}

export function redirectToLogin(): void {
  void redirectToSsoLogin({ forceLogin: hasAppLoggedOutFlag() });
}

export async function redirectToKeycloakLogin(): Promise<void> {
  await redirectToSsoLogin({ forceLogin: true });
}

export async function redirectToSsoLogin(options?: {
  forceLogin?: boolean;
}): Promise<void> {
  if (typeof window === "undefined") return;
  if (isSsoPrefetchRequest()) {
    notifySsoPrefetchComplete();
    return;
  }
  if (ssoLoginRedirectStarted) return;
  ssoLoginRedirectStarted = true;

  try {
    await registerPostLoginTargetForThisApp();
    window.location.replace(
      buildGatewayLoginUrlWithAppContext({
        forceLogin: options?.forceLogin ?? false,
      }),
    );
  } catch {
    ssoLoginRedirectStarted = false;
  }
}

export async function registerPostLoginTargetForThisApp(): Promise<void> {
  if (typeof window === "undefined") return;
  if (isSsoPrefetchRequest()) return;

  const base = resolveGatewayLoginApiBase();
  const { url: appUrl, name: appName, keycloakClientId, applicationId } =
    readThisAppLoginContext();

  try {
    await fetch(`${base}/login/select-app`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      signal: AbortSignal.timeout(1500),
      body: JSON.stringify({
        url: appUrl,
        name: appName,
        keycloakClientId,
        applicationId,
      }),
    });
  } catch {
    // Best-effort only.
  }
}

function resetKeycloakSession(): void {
  try {
    keycloak.clearToken?.();
  } catch {
    // ignore
  }
  keycloak.authenticated = false;
  keycloak.token = undefined;
  keycloak.refreshToken = undefined;
  keycloak.idToken = undefined;
}

export async function logoutFromThisApp(): Promise<void> {
  setAppLoggedOutFlag();
  const { logoutUrl, loginUrl } = await postGatewayLogout();
  clearAppSessionStorage();
  clearAuthSessionQuery();
  resetKeycloakSession();

  if (typeof window !== "undefined") {
    if (logoutUrl) {
      window.location.href = logoutUrl;
      return;
    }
    if (loginUrl) {
      window.location.href = loginUrl;
      return;
    }
  }

  await redirectToKeycloakLogin();
}

export async function signInViaSso(): Promise<void> {
  await redirectToSsoLogin({ forceLogin: hasAppLoggedOutFlag() });
}

export async function logoutOnSessionExpired(): Promise<void> {
  clearAllBrowserStorage();
  await logoutFromThisApp();
}

export function onKeycloakAuthLogout(): void {
  clearAllBrowserStorage();
}
